// @ts-nocheck
import React from 'react';

/**
 * Table primitives matching the reference table-card style. Compose like:
 *
 *   <Card padded={false}>
 *     <TableToolbar title="Visitors" count={128} right={<Button …/>} />
 *     <Table>
 *       <thead><tr><Th>Name</Th>…</tr></thead>
 *       <tbody>
 *         <tr><Td>…</Td>…</tr>
 *       </tbody>
 *     </Table>
 *     <TablePager … />
 *   </Card>
 *
 * Th supports `sortable`, `active`, `direction`, and calls `onSort` on click.
 */
const Table = ({ className = '', children }) => (
  <div className="overflow-x-auto">
    <table
      className={['w-full text-[13px]', className].join(' ')}
      style={{ borderCollapse: 'collapse' }}
    >
      {children}
    </table>
  </div>
);

export const Th = ({ children, sortable, active, direction = 'asc', onSort, align = 'left', style, ...rest }) => (
  <th
    onClick={sortable ? onSort : undefined}
    style={{
      textAlign: align,
      padding: '10px 18px',
      background: 'var(--mov-paper-2)',
      borderBottom: '1px solid var(--mov-line)',
      fontWeight: 500,
      color: 'var(--mov-ink-soft)',
      fontSize: 11.5,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      cursor: sortable ? 'pointer' : 'default',
      userSelect: 'none',
      ...style,
    }}
    {...rest}
  >
    {children}
    {sortable && (
      <span className="ml-1 inline-block" style={{ opacity: active ? 1 : 0.4 }} aria-hidden="true">
        {direction === 'desc' ? '▾' : '▴'}
      </span>
    )}
  </th>
);

export const Td = ({ children, align = 'left', mono, mute, style, className = '', ...rest }) => (
  <td
    className={[mono ? 'mov-mono' : '', className].join(' ')}
    style={{
      padding: '14px 18px',
      borderBottom: '1px solid var(--mov-line)',
      verticalAlign: 'middle',
      textAlign: align,
      color: mute ? 'var(--mov-ink-soft)' : 'var(--mov-ink)',
      fontSize: mono ? 12 : 13,
      ...style,
    }}
    {...rest}
  >
    {children}
  </td>
);

export const Tr = ({ children, hoverable = true, onClick, className = '', ...rest }) => (
  <tr
    onClick={onClick}
    className={[hoverable ? 'mov-row' : '', className].join(' ')}
    style={onClick ? { cursor: 'pointer' } : undefined}
    {...rest}
  >
    {children}
  </tr>
);

export const TableToolbar = ({ title, count, right, className = '' }) => (
  <div
    className={['flex items-center gap-3', className].join(' ')}
    style={{ padding: '16px 20px', borderBottom: '1px solid var(--mov-line)' }}
  >
    {title && <h3 className="m-0 text-[14.5px] font-semibold">{title}</h3>}
    {typeof count === 'number' && (
      <span className="mov-mono" style={{ fontSize: 11, color: 'var(--mov-ink-mute)', letterSpacing: '0.06em' }}>
        {count.toLocaleString()} total
      </span>
    )}
    {right && <div className="ml-auto flex items-center gap-2">{right}</div>}
  </div>
);

export const TablePager = ({ page, totalPages, total, onChange, info, className = '' }) => {
  if (!totalPages || totalPages <= 1) {
    if (!info) return null;
  }
  const go = (p) => onChange && onChange(Math.max(1, Math.min(totalPages, p)));
  const pages = [];
  if (totalPages) {
    const max = Math.min(totalPages, 7);
    const start = Math.max(1, Math.min(totalPages - max + 1, page - 3));
    for (let i = 0; i < max; i += 1) pages.push(start + i);
  }
  return (
    <div
      className={['flex items-center justify-between', className].join(' ')}
      style={{ padding: '12px 18px', borderTop: '1px solid var(--mov-line)', background: 'var(--mov-paper-2)' }}
    >
      <div className="text-[12px] text-mov-ink-mute">
        {info || (total != null ? `${total.toLocaleString()} total` : '')}
      </div>
      {totalPages > 1 && (
        <div className="flex gap-1">
          <PagerBtn onClick={() => go(page - 1)} disabled={page <= 1}>‹</PagerBtn>
          {pages.map((p) => (
            <PagerBtn key={p} on={p === page} onClick={() => go(p)}>{p}</PagerBtn>
          ))}
          <PagerBtn onClick={() => go(page + 1)} disabled={page >= totalPages}>›</PagerBtn>
        </div>
      )}
    </div>
  );
};

const PagerBtn = ({ on, disabled, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
    style={{
      width: 30, height: 30,
      borderRadius: 7,
      border: '1px solid var(--mov-line-strong)',
      background: on ? 'var(--mov-ink)' : 'var(--mov-paper)',
      color: on ? '#fff' : 'var(--mov-ink-soft)',
      fontSize: 12,
    }}
  >
    {children}
  </button>
);

export default Table;
